// ── BCH Live — Watch Page ─────────────────────────────────────────
//
// A composition layer. Every hard part lives in a focused module:
//
//   useStreamStatus   — relay pool, heartbeat polling, liveness
//   useStreamTips     — tips, goals, supporters, follower alerts
//   LivePlayer        — embed/HLS engines + theater/PiP/quality chrome
//   LiveChatPanel     — NIP-53 chat with threads, reactions, badges
//   OverlayLayer      — creator-toggleable on-player overlays
//   CreatorPanel      — identity, stats, scheduled + past broadcasts
//   BCHSupportPanels  — Super Chats, goals, supporters, quick tips
//
// The page's own job is layout, theater mode, and wiring BCH money flow
// into the presentation.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Radio, VideoOff, Eye, Share2, Check, ArrowLeft, ExternalLink,
  Sparkles, Settings2, X, Clock, Calendar, Users, MessageCircle,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useTipWallet } from '../hooks/useTipWallet';
import { prefetchBCHAddress } from '../hooks/useBCHAddress';
import {
  useStreamStatus, useResolvePubkey, useConnectionHealth,
} from '../hooks/useStreamStatus';
import { useStreamTips } from '../hooks/useStreamTips';
import {
  STREAM_EVENT_KIND, detectPlatform, slog, swarn,
} from '../lib/streamCredentials';
import {
  toLiveStream, getOverlayPrefs, setOverlayPrefs, OVERLAY_CONFIGS,
  formatDuration, formatScheduledTime, countdownTo,
  type OverlayPrefs,
} from '../lib/liveStream';
import LivePlayer from '../components/live/LivePlayer';
import LiveChatPanel from '../components/live/LiveChatPanel';
import OverlayLayer from '../components/live/StreamOverlays';
import SuperChatModal from '../components/live/SuperChatModal';
import CreatorPanel from '../components/live/CreatorPanel';
import {
  SupportersBar, SuperChatFeed, DonationGoalCard, TipBurst,
  QuickTipRow, CrossPromoRow,
} from '../components/live/BCHSupportPanels';

// ══════════════════════════════════════════════════════════════════
//  SMALL PIECES
// ══════════════════════════════════════════════════════════════════

function ConnectionIndicator() {
  const { connectedCount, status, total } = useConnectionHealth();
  // Only surface degradation — a healthy pool needs no chrome.
  if (status === 'good') return null;

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold ${
        status === 'poor' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'
      }`}
      title="Relay connection health"
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          status === 'poor' ? 'bg-red-500' : 'animate-pulse bg-amber-500'
        }`}
      />
      {connectedCount}/{total} relays
    </div>
  );
}

function ShareButton({ username, pubkey }: { username?: string; pubkey?: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${username ? `/live/${username}` : `/live/pk/${pubkey}`}`;

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: 'BCH Live', url }).catch(() => {});
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <button
      onClick={share}
      className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Share2 className="h-3.5 w-3.5" />}
      {copied ? 'Copied!' : 'Share'}
    </button>
  );
}

/** Elapsed runtime, ticking every second. */
function LiveDuration({ startsAt }: { startsAt: number }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!startsAt) return;
    const i = setInterval(() => force(n => n + 1), 1000);
    return () => clearInterval(i);
  }, [startsAt]);
  if (!startsAt) return null;
  return (
    <span className="flex items-center gap-1 text-xs font-semibold tabular-nums text-muted-foreground">
      <Clock className="h-3.5 w-3.5" />
      {formatDuration(startsAt)}
    </span>
  );
}

/** Big countdown for an upcoming broadcast. */
function ScheduledHero({ startsAt, title }: { startsAt: number; title: string }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const i = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(i);
  }, []);
  const cd = useMemo(() => countdownTo(startsAt, now), [startsAt, now]);

  const parts: Array<[number, string]> = [
    [cd.days, 'days'], [cd.hours, 'hrs'], [cd.mins, 'min'], [cd.secs, 'sec'],
  ];

  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/5 p-6 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
        <Calendar className="h-6 w-6 text-primary" />
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-primary/80">
        Scheduled broadcast
      </p>
      <h2 className="mx-auto mt-1 max-w-md text-base font-extrabold">{title}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{formatScheduledTime(startsAt)}</p>

      {cd.expired ? (
        <p className="mt-4 text-sm font-bold text-primary">Starting now…</p>
      ) : (
        <div className="mt-4 flex items-center justify-center gap-2">
          {parts.map(([v, label]) => (
            <div key={label} className="min-w-[3.5rem] rounded-xl bg-card px-2 py-2 shadow-sm">
              <p className="text-lg font-extrabold tabular-nums">{String(v).padStart(2, '0')}</p>
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                {label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  OVERLAY SETTINGS (creator-only)
// ══════════════════════════════════════════════════════════════════

function OverlaySettings({
  prefs, onChange, onClose,
}: { prefs: OverlayPrefs; onChange: (p: OverlayPrefs) => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="sc-modal-enter relative w-full max-w-sm overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl sm:rounded-3xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-sm font-extrabold">
            <Settings2 className="h-4 w-4 text-primary" /> Stream overlays
          </h2>
          <button onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="px-5 pt-3 text-[11px] leading-relaxed text-muted-foreground">
          Choose what appears over your stream. Saved on this device.
        </p>

        <div className="safe-area-bottom space-y-1 p-3">
          {OVERLAY_CONFIGS.map(c => (
            <label
              key={c.id}
              className="flex cursor-pointer items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-muted/60"
            >
              <input
                type="checkbox"
                checked={prefs[c.id]}
                onChange={e => onChange({ ...prefs, [c.id]: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-500"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold">{c.label}</span>
                <span className="block text-[11px] text-muted-foreground">{c.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════

export default function LiveStreamPage() {
  const { username, pubkey: directPubkey } = useParams<{ username: string; pubkey: string }>();
  const { pubkey: resolvedPubkey, loading: resolvingPubkey } =
    useResolvePubkey(directPubkey ? undefined : username);
  const pubkey = directPubkey || resolvedPubkey;

  const { user } = useAppContext();
  const { profile } = useNostrProfile(pubkey || undefined);
  const { stream, loading: streamLoading, tags } = useStreamStatus(pubkey);
  const tipWallet = useTipWallet();
  const connection = useConnectionHealth();

  const isOwner = !!user && !!pubkey && user.pubkey === pubkey;

  // Heartbeat age powers the diagnostics panel's "broadcaster stopped" check.
  // Recomputed each render (cheap) using the freshest of the beat/start/event
  // timestamps — mirroring how liveness itself is derived.
  const heartbeatAge = useMemo(() => {
    if (!stream) return Infinity;
    const beat = Math.max(
      parseInt(stream.heartbeat || '0', 10) || 0,
      parseInt(stream.startedAt || '0', 10) || 0,
      stream.created_at || 0,
    );
    if (!beat) return Infinity;
    return Math.max(0, Math.floor(Date.now() / 1000) - beat);
  }, [stream]);

  // ── Debug banner, once per page load ──
  useEffect(() => {
    slog('page', `viewer mounted — username=${username || '(none)'} pubkey=${(directPubkey || '(resolving)').slice(0, 12)}`);
    slog('page', 'stream logging is ON. To silence: localStorage.setItem("bchnostr:stream-debug","0") then reload');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Preload wallet + creator address so Super Chats are instant ──
  useEffect(() => {
    if (user && pubkey) {
      tipWallet.init().catch(() => {});
      prefetchBCHAddress(pubkey);
    }
    // tipWallet identity is stable; re-running on its state churn would
    // fire redundant init() calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pubkey]);

  // ── Derived stream facts ──
  const enriched = useMemo(
    () => (stream ? toLiveStream(stream, tags) : null),
    [stream, tags],
  );

  const isLive = stream?.isLive || false;
  const streamUrl = stream?.streamingUrl || '';
  const platformInfo = stream?.platformInfo || detectPlatform(streamUrl);
  const isUpcoming = !!enriched?.isUpcoming;

  // Chat is scoped by the stream's `a` coordinate. Creator Studio publishes
  // kind 30311 with d == the creator's pubkey, so that's the correct fallback
  // while the stream event is still loading — a `:` fallback would point at a
  // different coordinate and silently split the chat room in two.
  const aTag = stream?.aTag || (pubkey ? `${STREAM_EVENT_KIND}:${pubkey}:${pubkey}` : '');

  const checking = (directPubkey ? false : resolvingPubkey) || streamLoading;
  const displayName = username || directPubkey?.slice(0, 12);
  const creatorName = profile?.display_name || profile?.name || displayName;

  // A vanity URL whose NIP-05 the directory doesn't map to anyone. Resolution
  // has finished and still produced no pubkey, so there is nothing to show.
  const unresolvedUsername = !directPubkey && !resolvingPubkey && !pubkey;

  // Live + embeddable URL ⇒ show the player. No latching needed: liveness no
  // longer flaps, and LivePlayer protects its own iframe from re-renders.
  const showPlayer = isLive && !!streamUrl;

  useEffect(() => {
    if (isLive && !streamUrl) {
      swarn('player', 'stream is live but has no `streaming` URL — nothing to embed');
    }
  }, [isLive, streamUrl]);

  // ── BCH money layer ──
  const {
    tips, liveTips, newIds, supporters, totalUsd, goal,
    highlight, clearHighlight, newFollower, clearFollower, refetch: refetchTips,
  } = useStreamTips(pubkey, { watchFollowers: isOwner });

  const [showSuperChat, setShowSuperChat] = useState(false);
  const [showOverlaySettings, setShowOverlaySettings] = useState(false);
  const [theater, setTheater] = useState(false);
  // Chat visibility in theater mode on narrow screens, where there's no room
  // for a permanent sidebar.
  const [theaterChat, setTheaterChat] = useState(false);

  // ── Overlay prefs are the CREATOR's choice, applied for all viewers ──
  const [prefs, setPrefs] = useState<OverlayPrefs>(() => getOverlayPrefs(pubkey || ''));
  useEffect(() => {
    if (pubkey) setPrefs(getOverlayPrefs(pubkey));
  }, [pubkey]);

  const savePrefs = useCallback((next: OverlayPrefs) => {
    setPrefs(next);
    if (pubkey) setOverlayPrefs(pubkey, next);
  }, [pubkey]);

  // ── Theater mode locks page scroll so the player owns the viewport ──
  useEffect(() => {
    if (!theater) return;
    document.documentElement.classList.add('theater-lock');
    return () => document.documentElement.classList.remove('theater-lock');
  }, [theater]);

  // Newest tip id drives the coin burst.
  const burstKey = liveTips[0]?.id || null;

  const canSuperChat = !!user && !!pubkey && user.pubkey !== pubkey;

  const overlay = (
    <>
      <OverlayLayer
        prefs={prefs}
        tips={liveTips}
        goal={goal ? { raisedUsd: goal.raisedUsd, targetUsd: goal.targetUsd, title: goal.title } : null}
        newFollower={newFollower}
        onFollowerDone={clearFollower}
        highlightTip={highlight}
        onHighlightDone={clearHighlight}
      />
      <TipBurst trigger={burstKey} />
    </>
  );

  if (!username && !directPubkey) return null;

  // ── Theater layout ──
  //
  // The player takes the whole viewport with chat docked beside it on wide
  // screens. On narrow screens there isn't room for both, so chat becomes a
  // toggleable bottom sheet instead of being silently unavailable.
  if (theater) {
    return (
      <div className="fixed inset-0 z-40 flex flex-col bg-black">
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="relative flex min-h-0 flex-1 items-center justify-center">
            <LivePlayer
              embedUrl={platformInfo.embedUrl}
              platform={platformInfo.platform}
              originalUrl={platformInfo.originalUrl}
              isLive={isLive}
              title={stream?.title}
              overlay={overlay}
              theater
              onTheaterChange={setTheater}
              diagnostics={{ platformInfo, heartbeatAge, connection, isOwner }}
            />

            {/* Chat toggle — only meaningful where the sidebar is hidden */}
            <button
              onClick={() => setTheaterChat(c => !c)}
              aria-label={theaterChat ? 'Hide chat' : 'Show chat'}
              className="absolute right-3 top-3 z-30 rounded-lg bg-black/70 p-2 text-white/85 backdrop-blur-sm transition-colors hover:bg-black/85 hover:text-white lg:hidden"
            >
              {theaterChat ? <X className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
            </button>
          </div>

          {/* Wide screens: docked sidebar */}
          <aside className="hidden h-full w-[340px] shrink-0 border-l border-white/10 p-2 lg:block">
            <LiveChatPanel aTag={aTag} creatorPubkey={pubkey || ''} className="h-full" />
          </aside>

          {/* Narrow screens: bottom sheet */}
          {theaterChat && (
            <div className="min-h-0 flex-1 border-t border-white/10 p-2 lg:hidden">
              <LiveChatPanel aTag={aTag} creatorPubkey={pubkey || ''} className="h-full" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/live" aria-label="Back to BCH Live" className="-ml-1 rounded-lg p-1 hover:bg-muted">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="flex min-w-0 items-center gap-2 text-lg font-extrabold">
              <Radio className="h-5 w-5 shrink-0 text-red-500" />
              <span className="truncate">{creatorName}</span>
              {isLive && (
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-bold text-red-500">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> LIVE
                </span>
              )}
              {isLive && platformInfo.platform !== 'unknown' && platformInfo.platform !== 'hls' && (
                <span
                  className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase text-white"
                  style={{ backgroundColor: platformInfo.color }}
                >
                  {platformInfo.label}
                </span>
              )}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <ConnectionIndicator />
            {isOwner && (
              <button
                onClick={() => setShowOverlaySettings(true)}
                title="Stream overlays"
                aria-label="Stream overlay settings"
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Settings2 className="h-4 w-4" />
              </button>
            )}
            {isLive && streamUrl && (
              <a
                href={platformInfo.originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open on source platform"
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Player ── */}
      {showPlayer && (
        <div className="px-4 pt-4">
          <LivePlayer
            embedUrl={platformInfo.embedUrl}
            platform={platformInfo.platform}
            originalUrl={platformInfo.originalUrl}
            isLive={isLive}
            title={stream?.title}
            overlay={overlay}
            theater={false}
            onTheaterChange={setTheater}
            diagnostics={{ platformInfo, heartbeatAge, connection, isOwner }}
          />
          {/* Twitch renders its OWN mature-content gate ("Start Watching")
              inside the embed for flagged channels. It's a cross-origin
              iframe, so we can neither dismiss it nor click it for the
              viewer — a plain heads-up saves the "why won't it play" confusion. */}
          {platformInfo.platform === 'twitch' && (
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              If Twitch shows a content warning, tap <span className="font-semibold text-foreground">Start Watching</span> inside the player to begin. Tips and chat stay right here.
            </p>
          )}
        </div>
      )}

      {checking && !showPlayer ? (
        /* ── Loading skeleton ── */
        <div className="space-y-4 px-4 pt-4">
          <div className="live-skeleton aspect-video w-full rounded-2xl" />
          <div className="live-skeleton h-12 w-full rounded-2xl" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="live-skeleton h-72 rounded-2xl lg:col-span-2" />
            <div className="live-skeleton h-72 rounded-2xl" />
          </div>
        </div>
      ) : (
        <>
          {/* ── Title + meta ── */}
          {(isLive || isUpcoming) && stream && (
            <div className="px-4 pt-3">
              <h2 className="text-base font-extrabold leading-snug">{stream.title}</h2>
              {stream.summary && (
                <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
                  {stream.summary}
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {enriched?.category && (
                  <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                    {enriched.category.label}
                  </span>
                )}
                {isLive && stream.viewers && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" /> {stream.viewers}
                  </span>
                )}
                {isLive && <LiveDuration startsAt={enriched?.startsAt || 0} />}
                {supporters.length > 0 && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5" /> {supporters.length} supporter
                    {supporters.length === 1 ? '' : 's'}
                  </span>
                )}
                <span className="ml-auto">
                  <ShareButton username={username} pubkey={directPubkey} />
                </span>
              </div>

              {enriched && enriched.topics.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {enriched.topics.map(t => (
                    <span
                      key={t}
                      className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Super Chat CTA — the headline BCH action ── */}
          {isLive && canSuperChat && (
            <div className="space-y-2.5 px-4 pt-3">
              <button
                onClick={() => setShowSuperChat(true)}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-600 active:scale-[0.98]"
              >
                <Sparkles className="h-5 w-5" /> Send a Super Chat
              </button>
              <QuickTipRow
                creatorPubkey={pubkey || ''}
                aTag={aTag}
                onSent={() => setTimeout(refetchTips, 1500)}
              />
            </div>
          )}

          {isLive && !user && (
            <div className="px-4 pt-3">
              <div className="rounded-2xl border border-dashed border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-center">
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  Log in to chat and send instant Bitcoin Cash tips.
                </p>
              </div>
            </div>
          )}

          {/* ── Supporters rail ── */}
          {supporters.length > 0 && (
            <div className="px-4 pt-3">
              <SupportersBar supporters={supporters} totalUsd={totalUsd} />
            </div>
          )}

          {/* ── Scheduled broadcast ── */}
          {isUpcoming && enriched && (
            <div className="px-4 pt-4">
              <ScheduledHero startsAt={enriched.startsAt} title={enriched.title} />
            </div>
          )}

          {/* ── Username couldn't be resolved ── */}
          {/* Distinct from "offline": we never found out WHO this is, so
              claiming they're offline would be a guess. This is what shows
              when a /live/<username> link points at a NIP-05 that the
              directory doesn't map. */}
          {unresolvedUsername && (
            <div className="px-4 py-16 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-muted">
                <VideoOff className="h-10 w-10 text-muted-foreground/40" />
              </div>
              <h2 className="text-lg font-bold">Channel not found</h2>
              <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
                No verified account is registered to{' '}
                <span className="font-mono font-semibold">{username}@bchnostr.com</span>,
                so we can't tell whose channel this is.
              </p>
              <Link
                to="/live"
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90"
              >
                Browse live streams
              </Link>
            </div>
          )}

          {/* ── Offline ── */}
          {!unresolvedUsername && !isLive && !isUpcoming && !showPlayer && (
            <div className="px-4 py-16 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-muted">
                <VideoOff className="h-10 w-10 text-muted-foreground/40" />
              </div>
              <h2 className="text-lg font-bold">{creatorName} is offline</h2>
              <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
                Follow them to get a heads-up the moment they go live. Their chat
                and past broadcasts stay available below.
              </p>
              <Link
                to="/live"
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90"
              >
                Browse live streams
              </Link>
            </div>
          )}

          {/* ── Chat + sidebar ── */}
          <div className="grid grid-cols-1 gap-4 px-4 pb-6 pt-4 lg:grid-cols-3">
            <div className="flex flex-col lg:col-span-2" style={{ height: 520 }}>
              {aTag ? (
                <LiveChatPanel
                  aTag={aTag}
                  creatorPubkey={pubkey || ''}
                  className="h-full"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card text-center">
                  <MessageCircle className="h-8 w-8 text-muted-foreground/25" />
                  <p className="text-xs text-muted-foreground">Chat unavailable</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {goal && (
                <DonationGoalCard
                  goal={goal}
                  onSupport={canSuperChat ? () => setShowSuperChat(true) : undefined}
                />
              )}

              <SuperChatFeed
                tips={tips}
                newIds={newIds}
                canSuperChat={canSuperChat}
                onSuperChat={() => setShowSuperChat(true)}
              />

              {pubkey && (
                <CreatorPanel
                  pubkey={pubkey}
                  isLive={isLive}
                  totalTippedUsd={totalUsd}
                  supporterCount={supporters.length}
                  categoryLabel={enriched?.category?.label || null}
                />
              )}

              {pubkey && <CrossPromoRow creatorPubkey={pubkey} />}
            </div>
          </div>
        </>
      )}

      {/* ── Modals ── */}
      {showSuperChat && pubkey && (
        <SuperChatModal
          streamerPubkey={pubkey}
          aTag={aTag}
          onClose={() => setShowSuperChat(false)}
          onSent={() => setTimeout(refetchTips, 1500)}
        />
      )}

      {showOverlaySettings && (
        <OverlaySettings
          prefs={prefs}
          onChange={savePrefs}
          onClose={() => setShowOverlaySettings(false)}
        />
      )}
    </div>
  );
}
